import React, { ReactElement, useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  DroneHarmonyGeodataProcessType,
  GeodataBoundaryPolygonModel,
  GroundControlPoint,
} from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { FetchDroneHarmonyFlightPlanResponse } from '../../../../../../../typings/api/skymap/rest/v1/project';
import { WebsocketMessage } from '../../../../../../../typings/websocket-messages';
import { textUtils } from '../../../../../utils/text/text';
import { hasElements, isDefined } from '../../../../../utils/variables';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { useDialog } from '../../../../hooks/use-dialog';
import { useErrorHandling } from '../../../../hooks/use-error-handling';
import { useSubscribe } from '../../../../hooks/use-subscribe';
import { useToaster } from '../../../../state/toaster-state';
import { reset } from '../../../../utils/styled-reset';
import { Button } from '../../../button/button';
import {
  DataTable,
  DataTableColumns,
  DataTableProps,
  DataTableSortType,
} from '../../../data-table/data-table';
import { Icon } from '../../../icon/icon';
import { IconPanel } from '../../../icon-panel/icon-panel';
import { InfoBox } from '../../../info-box/info-box';
import { Stack } from '../../../stack/stack';
import { IntegrationTitle } from '../../integration-title';
import {
  DroneHarmonyIntegrationItem,
  IntegrationsContext,
  IntegrationStatus,
} from '../../state/integrations-state';
import { AddFlightPlanDialog } from './dialogs/add-flight-plan-dialog/add-flight-plan-dialog';
import { EditFlightPlanDialog } from './dialogs/edit-flight-plan-dialog';
import { useFetchFlightPlansQuery, useRemoveIntegrationQuery } from './hooks';
import NoFlightPlansAdded from './images/no_flight_plans_added.svg?react';

type FlightPlan = {
  id: string;
  guid: string;
  processingPackage: DroneHarmonyGeodataProcessType;
  generateTerrainModel: boolean;
  gcpAvailable: boolean;
  groundControlPoints?: GroundControlPoint[];
  boundaryPolygons?: GeodataBoundaryPolygonModel[];
};

type FlightPlanDataRow = {
  id: string;
  guid: string;
  processingPackage: string;
  gcpStatus: ReactElement;
};

const GuidWithCopy = ({ guid }: { guid: string }) => {
  const { t } = useTranslation();
  const { showToast } = useToaster();

  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      textUtils.copyTextToClipboard(guid);
      showToast({
        type: 'success',
        title: t('toaster.title.success', { ns: 'common' }),
        message: t('droneHarmony.guidCopySuccess', { ns: 'integrations' }),
      });
    },
    [guid, showToast, t],
  );

  return (
    <GuidFrame className="notranslate">
      <span>{guid}</span>
      <CopyIconWrapper>
        <Icon
          icon={['fal', 'copy']}
          stopPropagationOnClick={true}
          title={t('droneHarmony.copyGuid', { ns: 'integrations' })}
          onClick={handleCopy}
          onHoverStyle={{ icon: ['fad', 'copy'] }}
        />
      </CopyIconWrapper>
    </GuidFrame>
  );
};

const resolvePacketName = (value: string): string => {
  switch (value) {
    case 'FULL':
      return 'Standard';
    case 'ORTHO_ONLY':
      return 'Ortofoto';
    case 'FULL_HIGH_QUALITY':
      return 'Premium';
    case 'POINTCLOUD_ONLY':
      return 'Punktmoln';
    case 'SIGNATURE':
      return 'Signature';
    default:
      return 'Okänd typ';
  }
};

const columns: DataTableColumns<FlightPlanDataRow> = {
  guid: {
    alignment: 'left',
    sortableBy: { type: DataTableSortType.STRING },
    title: 'ID (guid)',
    unfilterable: true,
    width: '340px',
    render: (item: FlightPlanDataRow) => <GuidWithCopy guid={item.guid} />,
  },
  processingPackage: {
    sortableBy: { type: DataTableSortType.STRING },
    title: 'Paket',
    formatter: (value: string) => resolvePacketName(value),
    unfilterable: true,
  },
  gcpStatus: {
    title: 'GCP',
    alignment: 'left',
    unfilterable: true,
  },
};

type Props = {
  item: DroneHarmonyIntegrationItem;
};

const ActiveView = (props: Props) => {
  const [flightPlans, setFlightPlans] = React.useState<FlightPlan[]>([]);
  const [selectedFlightPlan, setSelectedFlightPlan] = React.useState<FlightPlan | undefined>();
  const addFlightPlanDialog = useDialog();
  const editFlightPlanDialog = useDialog();
  const { toggleIntegration, removeIntegration } = useContext(IntegrationsContext);
  const { handleError, buildErrorList } = useErrorHandling();

  if (props.item.status !== IntegrationStatus.Active) {
    throw new Error("Expected integration item status is not 'Active'.");
  }

  const dataTable: DataTableProps<FlightPlanDataRow> = useMemo(
    () => ({
      columns,
      items: flightPlans.map((flightPlan) => ({
        gcpStatus: flightPlan.gcpAvailable ? (
          <>
            <Icon icon={['fal', 'check']} key={flightPlan.id} title="GCP data" />
          </>
        ) : (
          <></>
        ),
        ...flightPlan,
      })),
    }),
    [flightPlans],
  );

  const removeIntegrationMutation = useRemoveIntegrationQuery({
    projectId: props.item.projectId,
    onError: (err) => {
      handleError(err);
    },
    onMutate: () => {
      toggleIntegration(props.item);
      removeIntegration(props.item);
    },
  });

  const onMutate = useCallback(
    (result: FetchDroneHarmonyFlightPlanResponse) => {
      const newFlightPlans: FlightPlan[] = result.data.map((flightPlan) => ({
        id: flightPlan.id,
        guid: flightPlan.missionId,
        processingPackage: flightPlan.geodataProcessType,
        generateTerrainModel: flightPlan.generateTerrainModel,
        gcpAvailable: hasElements(flightPlan.groundControlPoints),
        groundControlPoints: flightPlan.groundControlPoints,
        boundaryPolygons: flightPlan.boundaryPolygons,
      }));
      setFlightPlans(newFlightPlans);

      const updatedFlightPlan = newFlightPlans.find(
        (flightPlan) => flightPlan.id === selectedFlightPlan?.id,
      );

      if (updatedFlightPlan) {
        setSelectedFlightPlan(updatedFlightPlan);
      }
    },
    [selectedFlightPlan],
  );

  const { mutate: fetchFlightPlans } = useFetchFlightPlansQuery({
    projectId: props.item.projectId,
    onError: (err) => handleError(err),
    onMutate: onMutate,
  });

  const handleWebsocketMessage = useCallback(
    (event: WebsocketMessage) => {
      if (
        event.message === 'BoundaryPolygonUpdated' &&
        flightPlans.some((fp) => fp.id === event.droneHarmonyFlightPlanId)
      ) {
        fetchFlightPlans();
      }
    },
    [flightPlans, fetchFlightPlans],
  );

  useSubscribe(SubscriptionTopic.WebsocketMessageRecieved, handleWebsocketMessage);

  useEffect(() => {
    fetchFlightPlans();
  }, [fetchFlightPlans]);

  return (
    <>
      <Component>
        <Stack direction="column" spacing={2}>
          <Stack alignItems="start" direction="row" justifyContent="space-between" spacing={0.5}>
            <IntegrationTitle activationDate={props.item.createdAt}>
              {props.item.name}
            </IntegrationTitle>

            <Button
              color="error"
              variant={'text'}
              onClick={() => removeIntegrationMutation.mutate()}
            >
              Ta bort integration
            </Button>
          </Stack>

          <InfoBox color="yellow" leftIcon={{ icon: ['fad', 'info'] }}>
            Integrationen behöver information om vilka flygrutter i <strong>Drone Harmony</strong>{' '}
            molnprocesseringar skall skapas för. Klicka på knappen nedan för att lägga till en
            flygrutt.
            <br />
            <br />
            När en flygrutt har färdigställts hämtas bilder från drönaren och en molnprocessering
            skapas automatiskt utifrån det paket som är angivet för flygrutten.
          </InfoBox>

          <Stack spacing={0.5}>
            <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={0.5}>
              <span style={{ fontWeight: 500 }}>Flygrutter</span>
              <Button
                color="secondary"
                variant={'contained'}
                onClick={() => {
                  addFlightPlanDialog.show();
                }}
              >
                Lägg till flygrutt
              </Button>
            </Stack>

            <DataTable
              {...dataTable}
              fixedLayout={true}
              pageSize={undefined}
              tableStyle={{
                rowCell: {
                  fontSize: '14px',
                },
                headerCell: {
                  fontSize: '14px',
                },
              }}
              onRowClicked={(selectedFlightPlan: FlightPlanDataRow) => {
                setSelectedFlightPlan(
                  flightPlans.find((flightPlan) => flightPlan.id === selectedFlightPlan.id),
                );
                editFlightPlanDialog.show();
              }}
            />

            {flightPlans.length === 0 && (
              <Stack alignItems="center" spacing={0.5}>
                <IconPanel
                  header={<StyledNoFlightPlansAdded />}
                  responsiveness={{ compressed: false }}
                >
                  {{
                    title: 'Inga flygrutter har lagts till',
                  }}
                </IconPanel>
              </Stack>
            )}
          </Stack>
        </Stack>

        {buildErrorList()}
      </Component>

      {addFlightPlanDialog.render(
        <AddFlightPlanDialog
          onClose={() => {
            addFlightPlanDialog.hide();
            fetchFlightPlans();
          }}
        />,
      )}

      {isDefined(selectedFlightPlan) &&
        editFlightPlanDialog.render(() => (
          <EditFlightPlanDialog
            flightPlan={selectedFlightPlan}
            onClose={() => {
              editFlightPlanDialog.hide();
              setSelectedFlightPlan(undefined);
              fetchFlightPlans();
            }}
          />
        ))}
    </>
  );
};

// @ts-ignore
const StyledNoFlightPlansAdded = styled(NoFlightPlansAdded)`
  margin-top: 4em;
  width: 300px;
`;

const GuidFrame = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.color.gray.light};
  background: ${({ theme }) => theme.color.gray.lightest};
  font-family: monospace;
  font-size: 12px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 260px;
  }
`;

const CopyIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border-radius: 4px;
  cursor: pointer;
`;

const Component = styled.div`
  ${reset}
  font-size: 14px;
`;

export { ActiveView };
